import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import ReplyIcon from '@mui/icons-material/Reply';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import ForwardIcon from '@mui/icons-material/Forward';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { keyframes } from '@mui/system';
import ConfirmDialog from '../ui/Modal/ConfirmDialog';

const wiggle = keyframes`
  0% { transform: translateY(0) rotate(0deg); }
  35% { transform: translateY(-2px) rotate(-6deg); }
  70% { transform: translateY(0) rotate(0deg); }
  100% { transform: translateY(0) rotate(0deg); }
`;
const pulse = keyframes`
  0% { filter: brightness(1); }
  50% { filter: brightness(1.08); }
  100% { filter: brightness(1); }
`;

export interface ConciergeContextMenuProps {
  open: boolean;
  anchorPosition: { top: number; left: number } | undefined;
  onClose: () => void;

  // actions
  onReply: () => void;
  onCopyText: () => void;
  onSaveAs: () => void;
  onEdit: () => void;
  onPin: () => void;
  onForward: () => void;
  onSelect: () => void;
  onDelete: () => void;

  // states
  canCopyText?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  hasSingleImage?: boolean;

  // reactions
  showEmojiBar?: boolean;
  emojiOptions?: string[];
  onReact?: (emoji: string) => void;
}

export default function ContextMenu(props: ConciergeContextMenuProps) {
  const {
    open,
    anchorPosition,
    onClose,
    onReply,
    onCopyText,
    onSaveAs,
    onEdit,
    onPin,
    onForward,
    onSelect,
    onDelete,
    canCopyText = false,
    canEdit = false,
    canDelete = false,
    hasSingleImage = false,
    showEmojiBar = false,
    emojiOptions = [],
    onReact,
  } = props;

  const paperRef = React.useRef(null);
  const [emojiPos, setEmojiPos] = React.useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // robustly recompute emoji bar position based on the final placed menu paper
  React.useEffect(() => {
    let raf1 = 0;
    let raf2 = 0;
    let resizeObs;

    const recompute = () => {
      const el = paperRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;

      // Clamp within viewport horizontally and always place just above the paper
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const clampedLeft = Math.min(Math.max(centerX, 8), vw - 8);

      setEmojiPos({ top: rect.top, left: clampedLeft, placement: 'above' });
    };

    if (open) {
      // Let MUI/Popper finish measuring and flipping, then measure on two consecutive frames
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => {
          recompute();
        });
      });

      // Observe later changes in size/position of the paper (e.g., density/hover, content changes)
      if ('ResizeObserver' in window) {
        resizeObs = new ResizeObserver(() => recompute());
        // Observe both the paper and its parent (Popper) when possible
        const el = paperRef.current;
        if (el) resizeObs.observe(el);
        if (el && el.parentElement) resizeObs.observe(el.parentElement);
      }

      window.addEventListener('resize', recompute);
      window.addEventListener('scroll', recompute, true);

      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
        window.removeEventListener('resize', recompute);
        window.removeEventListener('scroll', recompute, true);
        if (resizeObs) resizeObs.disconnect();
      };
    } else {
      setEmojiPos(undefined);
    }
  }, [open]);

  return (
    <>
      {showEmojiBar && open && emojiPos && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'fixed',
            top: (emojiPos.top || 0),
            left: (emojiPos.left || 0),
            transform: 'translate(-50%, calc(-100% - 16px))',
            zIndex: (t) => (t.zIndex?.tooltip ?? 1500) + 50,
            pointerEvents: 'auto',
          }}
        >
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            pl: 1, pr: 0.5, py: 0.5,
            borderRadius: 999,
            bgcolor: 'rgba(255,255,255,0.6)',
            backdropFilter: 'saturate(180%) blur(16px)',
            WebkitBackdropFilter: 'saturate(180%) blur(16px)',
            border: '1px solid rgba(255,255,255,0.7)',
            boxShadow: '0 8px 24px rgba(15,23,42,0.18), 0 1px 0 rgba(255,255,255,0.55) inset',
          }}>
            {emojiOptions.map((e, idx) => (
              <Box key={e}
                onClick={(ev) => {
                  ev.stopPropagation();
                  try { onReact && onReact(e); } finally { onClose && onClose(); }
                }}
                sx={{
                  cursor: 'pointer',
                  px: 0.75, py: 0.25,
                  borderRadius: 999,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
                  fontSize: 16,
                  lineHeight: '16px',
                  transition: 'transform 80ms ease',
                  '&:active': { transform: 'scale(0.95)' },
                  animation: `${wiggle} 1600ms ease-in-out ${idx * 120}ms infinite, ${pulse} 2400ms ease-in-out ${idx * 120}ms infinite`,
                  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                }}
                title={`React ${e}`}
              >{e}</Box>
            ))}
            <Box
              sx={{
                ml: 0.25,
                width: 28, height: 28, borderRadius: '50%',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: 'rgba(255,255,255,0.8)',
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                cursor: 'default',
              }}
              aria-label="More"
              title="More"
            >
              <ChevronRightIcon sx={{ color: 'rgba(0,0,0,0.55)' }} fontSize="small" />
            </Box>
          </Box>
        </Box>
      )}

      <Menu
        open={open}
        onClose={onClose}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition}
        MenuListProps={{ dense: true, disablePadding: true }}
        PaperProps={{
          ref: paperRef,
          elevation: 4,
          sx: {
            borderRadius: 2,
            minWidth: 156,
            py: 0.25,
            px: 0.75,
            boxShadow: '0 10px 30px rgba(15,23,42,0.12), 0 1px 0 rgba(255,255,255,0.6) inset',
            border: '1px solid rgba(255,255,255,0.6)',
            bgcolor: 'rgba(255,255,255,0.35)',
            backdropFilter: 'saturate(180%) blur(16px)',
            WebkitBackdropFilter: 'saturate(180%) blur(16px)',
            backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.48), rgba(255,255,255,0.28))',
            '& .MuiMenuItem-root': { px: 0.75, py: 0.35, my: 0, gap: 0.5, borderRadius: 1 },
            '& .MuiMenuItem-root:hover': { bgcolor: '#F3F4F6' },
            '& .MuiListItemIcon-root': { minWidth: 20, color: 'text.secondary', mr: 0.5 },
            '& .MuiMenuItem-root .MuiSvgIcon-root': { fontSize: 17, opacity: 0.9 },
            '& .MuiMenuItem-root:not([data-danger="true"]) .MuiSvgIcon-root': { color: 'text.secondary' },
            '& .MuiMenuItem-root[data-danger="true"] .MuiSvgIcon-root': { color: '#DC2626' },
            '& .MuiMenuItem-root[data-danger="true"] .MuiListItemText-primary': { color: '#DC2626', fontWeight: 500 },
            '& .MuiListItemText-primary': { fontSize: 14, lineHeight: 1.35, letterSpacing: 0.1, color: '#0F172A', fontWeight: 400, mr: 0.25 },
            '& .MuiDivider-root': { my: 0.3, opacity: 0.5 },
          },
        }}
      >
        <MenuItem onClick={onReply}>
          <ListItemIcon><ReplyIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Reply" />
        </MenuItem>
        <MenuItem onClick={onCopyText} disabled={!canCopyText}>
          <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Copy Text" />
        </MenuItem>
        <Divider />
        {hasSingleImage && (
          <MenuItem onClick={onSaveAs}>
            <ListItemIcon><ImageOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Copy Media" />
          </MenuItem>
        )}
        <MenuItem onClick={onSaveAs}>
          <ListItemIcon><SaveAltIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Save As…" />
        </MenuItem>
        <MenuItem onClick={onEdit} disabled={!canEdit}>
          <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Edit" />
        </MenuItem>
        <MenuItem onClick={onPin}>
          <ListItemIcon><PushPinOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Pin" />
        </MenuItem>
        <MenuItem onClick={onForward}>
          <ListItemIcon><ForwardIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Forward" />
          <ChevronRightIcon fontSize="small" sx={{ ml: 0.5, color: 'text.secondary' }} />
        </MenuItem>
        <MenuItem onClick={onSelect}>
          <ListItemIcon><CheckCircleOutlineIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Select" />
        </MenuItem>
        <Divider />
        <MenuItem data-danger="true" onClick={() => {
          setConfirmOpen(true);
          onClose && onClose();
        }} sx={{ color: canDelete ? '#DC2626' : 'text.disabled' }} disabled={!canDelete}>
          <ListItemIcon sx={{ minWidth: 24 }}>
            <DeleteOutlineIcon fontSize="small" sx={{ color: '#DC2626' }} />
          </ListItemIcon>
          <ListItemText primary="Delete" />
        </MenuItem>
      </Menu>
      <ConfirmDialog
        open={confirmOpen}
        title="Delete messages?"
        description="Delete the selected messages. This can’t be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          // deletion continues in background; dialog closes instantly via onCancel inside ConfirmDialog
          setConfirmOpen(false);
          onDelete && onDelete();
        }}
      />
    </>
  );
}
